import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { tradingApi } from '../services/api';
import { Clock } from 'lucide-react';

export const IBTickerTape: React.FC = () => {
    const { token } = useAuth();
    const [marketStatus, setMarketStatus] = useState<string>("");
    const [statusColor, setStatusColor] = useState<string>("text-muted");
    const [tomorrowStatus, setTomorrowStatus] = useState<string>("");

    const fetchMarketStatus = useCallback(async () => {
        if (!token) return;
        try {
            const result = await tradingApi.getMarketStatus();
            const liquidHours = result.data?.liquid_hours;
            if (result.success && liquidHours) {
                        const now = new Date();
                        const nyOptions: Intl.DateTimeFormatOptions = {
                            timeZone: "America/New_York",
                            year: 'numeric', month: '2-digit', day: '2-digit'
                        };
                        const formatter = new Intl.DateTimeFormat('en-US', nyOptions);

                        // --- Tomorrow Logic ---
                        const tomorrow = new Date(now);
                        tomorrow.setDate(tomorrow.getDate() + 1);
                        const tParts = formatter.formatToParts(tomorrow);
                        const ty = tParts.find(p => p.type === 'year')?.value;
                        const tm = tParts.find(p => p.type === 'month')?.value;
                        const td = tParts.find(p => p.type === 'day')?.value;
                        const tomorrowStr = `${ty}${tm}${td}`;

                        const segments = liquidHours.split(';');
                        const tomorrowSegment = segments.find((s: string) => s.startsWith(tomorrowStr));

                        if (tomorrowSegment) {
                            if (tomorrowSegment.includes("CLOSED")) {
                                setTomorrowStatus("Tomorrow: Closed");
                            } else {
                                setTomorrowStatus("Tomorrow: Regular");
                            }
                        } else {
                            const day = tomorrow.getDay();
                            if (day === 0 || day === 6) setTomorrowStatus("Tomorrow: Closed");
                            else setTomorrowStatus("Tomorrow: Regular");
                        }

                        // --- Today Logic ---
                        const parts = formatter.formatToParts(now);
                        const y = parts.find(p => p.type === 'year')?.value;
                        const m = parts.find(p => p.type === 'month')?.value;
                        const d = parts.find(p => p.type === 'day')?.value;
                        const todayStr = `${y}${m}${d}`; // YYYYMMDD

                        const todaySegment = segments.find((s: string) => s.startsWith(todayStr));

                        if (todaySegment) {
                            if (todaySegment.includes("CLOSED")) {
                                setMarketStatus("US Market: Closed (Holiday)");
                                setStatusColor("text-red-400");
                                return;
                            }

                            const [start, end] = todaySegment.split('-');
                            const [, startTime] = start.split(':');
                            const [, endTime] = end.split(':');

                            const getSeconds = (timeStr: string) => {
                                const h = parseInt(timeStr.substring(0, 2));
                                const m = parseInt(timeStr.substring(2, 4));
                                return h * 3600 + m * 60;
                            };

                            const openSeconds = getSeconds(startTime);
                            const closeSeconds = getSeconds(endTime);

                            const nyTimeOptions: Intl.DateTimeFormatOptions = {
                                timeZone: "America/New_York",
                                hour12: false,
                                hour: 'numeric', minute: 'numeric', second: 'numeric'
                            };
                            const timeFormatter = new Intl.DateTimeFormat('en-US', nyTimeOptions);
                            const timeParts = timeFormatter.formatToParts(now);
                            const curH = parseInt(timeParts.find(p => p.type === 'hour')?.value || '0');
                            const curM = parseInt(timeParts.find(p => p.type === 'minute')?.value || '0');
                            const curS = parseInt(timeParts.find(p => p.type === 'second')?.value || '0');
                            const totalSeconds = curH * 3600 + curM * 60 + curS;

                            if (totalSeconds < openSeconds) {
                                const diff = openSeconds - totalSeconds;
                                const h = Math.floor(diff / 3600);
                                const m = Math.floor((diff % 3600) / 60);
                                const s = diff % 60;
                                setMarketStatus(`US Market: Opens in ${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`);
                                setStatusColor("text-yellow-400");
                            } else if (totalSeconds >= openSeconds && totalSeconds < closeSeconds) {
                                const diff = closeSeconds - totalSeconds;
                                const h = Math.floor(diff / 3600);
                                const m = Math.floor((diff % 3600) / 60);
                                const s = diff % 60;
                                setMarketStatus(`US Market: Open • Closes in ${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`);
                                setStatusColor("text-neon");
                            } else {
                                setMarketStatus("US Market: Closed");
                                setStatusColor("text-red-400");
                            }
                            return;
                        }
            }
        } catch (err) {
            console.warn("Failed to fetch market status", err);
        }

        updateMarketStatusLocal();
    }, [token]);

    const updateMarketStatusLocal = () => {
        const now = new Date();
        const nyOptions: Intl.DateTimeFormatOptions = {
            timeZone: "America/New_York",
            hour12: false,
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            weekday: 'short'
        };
        const formatter = new Intl.DateTimeFormat('en-US', nyOptions);
        const parts = formatter.formatToParts(now);

        const getPart = (type: string) => parts.find(p => p.type === type)?.value;
        const hour = parseInt(getPart('hour') || '0');
        const minute = parseInt(getPart('minute') || '0');
        const second = parseInt(getPart('second') || '0');
        const weekday = getPart('weekday');

        const dayIdx = now.getDay();
        if (dayIdx === 5 || dayIdx === 6) setTomorrowStatus("Tomorrow: Closed (Weekend)");
        else setTomorrowStatus("Tomorrow: Regular");

        const totalSeconds = hour * 3600 + minute * 60 + second;
        const openSeconds = 9 * 3600 + 30 * 60;
        const closeSeconds = 16 * 3600;

        const isWeekend = weekday === 'Sat' || weekday === 'Sun';

        if (isWeekend) {
            setMarketStatus("US Market: Closed (Weekend)");
            setStatusColor("text-red-400");
        } else if (totalSeconds < openSeconds) {
            const diff = openSeconds - totalSeconds;
            const h = Math.floor(diff / 3600);
            const m = Math.floor((diff % 3600) / 60);
            const s = diff % 60;
            setMarketStatus(`US Market: Opens in ${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`);
            setStatusColor("text-yellow-400");
        } else if (totalSeconds >= openSeconds && totalSeconds < closeSeconds) {
            const diff = closeSeconds - totalSeconds;
            const h = Math.floor(diff / 3600);
            const m = Math.floor((diff % 3600) / 60);
            const s = diff % 60;
            setMarketStatus(`US Market: Open • Closes in ${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`);
            setStatusColor("text-neon");
        } else {
            setMarketStatus("US Market: Closed");
            setStatusColor("text-red-400");
        }
    };

    useEffect(() => {
        fetchMarketStatus();
        // Market hours do not change every second. Avoid a global API/Intl parsing
        // loop while keeping the countdown responsive enough for the header.
        const statusInterval = setInterval(fetchMarketStatus, 5000);

        return () => {
            clearInterval(statusInterval);
        };
    }, [fetchMarketStatus]);

    return (
        <div className="w-full overflow-hidden flex items-center justify-center">
            <div className="flex items-center gap-2">
                <Clock size={14} className={statusColor} />
                <span className={`text-xs font-bold uppercase tracking-wider ${statusColor} whitespace-nowrap`}>
                    {marketStatus}
                </span>
                {tomorrowStatus && (
                    <>
                        <span className="text-white/20 mx-1">•</span>
                        <span className="text-[10px] sm:text-xs font-medium text-muted uppercase tracking-wider whitespace-nowrap">
                            {tomorrowStatus}
                        </span>
                    </>
                )}
            </div>
        </div>
    );
};
